import React, { useState } from 'react';
import { Dropdown, IconButton } from 'components/core';
import { MultiAddressQuantityDistributorProps } from './MultiAddressQuantityDistributor.types';
import { AddressSearch } from '../AddressSearch';
import Image from 'next/image';
import * as Styled from './MultiAddressQuantityDistributor.styles';
import { useRetrieveDsoShippingAddresses } from 'reactQuery/queries';
import { useI18n } from 'contexts/i18n';
import PriceColumn from '../DSOShoppingCart/PriceColumn';
import {
  buildDiscountLabel,
  hasBulkDiscount,
} from '../DSOShoppingCart/DSOShoppingCart.helper';
import { useIsMutating } from 'react-query';

const COMPACT_ROW_THRESHOLD = 5;

const PRICING_GRID = '0.5fr 2fr 0.5fr 0.5fr 0.5fr';

const MultiAddressQuantityDistributor: React.FC<
  MultiAddressQuantityDistributorProps
> = ({
  allowableQuantities,
  addressRows,
  setAddressRows,
  showPricing,
  highlightEmptyAddresses,
  onAddressChange,
  onAddRow,
  onRowUpdate,
  onRemoveRow,
  allowDeleteFirstRow = false,
  isClearable = true,
  noBorder = false,
  quantityWidth = '100%',
}) => {
  const i18n = useI18n();
  const isMutating = useIsMutating();
  const { data: shippingAddresses, isLoading: isShippingLoading } =
    useRetrieveDsoShippingAddresses();
  const [showAddMoreError, setShowAddMoreError] = useState(false);

  const updateQuantity = (index: number, value: string) => {
    const updated = [...addressRows];
    updated[index].quantity = value;
    setAddressRows(updated);
    onRowUpdate?.(updated[index], index);
  };

  const updateAddressRow = (index: number, accountNumber: string) => {
    const updated = [...addressRows];
    updated[index].accountNumber = accountNumber;
    setAddressRows(updated);

    if (accountNumber && showAddMoreError) {
      setShowAddMoreError(false);
    }

    if (accountNumber) {
      onAddressChange?.();
    }

    onRowUpdate?.(updated[index], index);
  };

  const addAddressRow = () => {
    const hasEmptyRow = addressRows.some(row => !row.accountNumber);
    if (hasEmptyRow) {
      setShowAddMoreError(true);
      return; // Don't add a new row if there's already an empty one
    }

    const defaultQuantity = allowableQuantities[0].value;
    setAddressRows([
      ...addressRows,
      { quantity: defaultQuantity, accountNumber: '' },
    ]);
    onAddRow?.(defaultQuantity);
  };

  const removeAddressRow = (index: number) => {
    const removedRow = addressRows[index];
    setAddressRows(addressRows.filter((_, i) => i !== index));
    setShowAddMoreError(false);
    onRemoveRow?.(removedRow, index);
  };

  const getExcludedAddresses = (currentIndex: number) => {
    return addressRows
      .map((row, index) => (index !== currentIndex ? row.accountNumber : null))
      .filter(Boolean) as string[];
  };

  const isCompact = addressRows.length > COMPACT_ROW_THRESHOLD;

  return (
    <Styled.MultiShipWrapper>
      <Styled.RowListScroll role="list" aria-label="Shipping address rows">
        {addressRows.map((row, index) => (
          <Styled.Row
            key={index}
            role="listitem"
            $compact={isCompact}
            $customGridTemplate={showPricing ? PRICING_GRID : undefined}
            aria-label={`Address ${index + 1}, quantity ${row.quantity}`}
          >
            <Styled.RowQtyCell>
              <Dropdown
                options={allowableQuantities}
                selectedValue={row.quantity}
                onChange={val => updateQuantity(index, val)}
                label="Qty"
                size="sm"
                width={quantityWidth}
                noBorder={noBorder}
              />
            </Styled.RowQtyCell>
            <Styled.RowAddressCell>
              <AddressSearch
                addresses={shippingAddresses ?? []}
                isLoading={isShippingLoading}
                excludedAddresses={getExcludedAddresses(index)}
                selectedValue={row.accountNumber}
                onChange={accountNumber => {
                  if (accountNumber) {
                    updateAddressRow(index, accountNumber);
                  } else {
                    updateAddressRow(index, '');
                  }
                }}
                placeholder="Select Shipping Address"
                width="100%"
                hasError={
                  (highlightEmptyAddresses || showAddMoreError) &&
                  !row.accountNumber?.trim()
                }
                isClearable={isClearable}
                noBorder={noBorder}
                showAccountNumber
              />
            </Styled.RowAddressCell>
            {showPricing && (
              <>
                <div>{row.displayUnitPrice}</div>
                <PriceColumn
                  subtotal={row.displaySubTotal || row.displayPrice || ''}
                  netTotal={row.displayPrice || ''}
                  showDiscount={hasBulkDiscount(row.discountPercentage)}
                />
              </>
            )}
            <Styled.RowActionCell>
              {(index > 0 || allowDeleteFirstRow) && (
                <IconButton
                  onClick={() => removeAddressRow(index)}
                  showBorder={false}
                  aria-label={`Remove address ${index + 1}`}
                >
                  <Image
                    src="/images/icons/delete.svg"
                    alt="Delete"
                    width="24"
                    height="24"
                  />
                </IconButton>
              )}
            </Styled.RowActionCell>
          </Styled.Row>
        ))}
      </Styled.RowListScroll>

      <Styled.AddMoreWrapper>
        <Styled.AddMore
          onClick={addAddressRow}
          type="button"
          disabled={isMutating > 0}
        >
          + Ship to more addresses
        </Styled.AddMore>
        {showAddMoreError && (
          <Styled.AddMoreError>
            Please select an address for the existing row before adding more.
          </Styled.AddMoreError>
        )}
      </Styled.AddMoreWrapper>
    </Styled.MultiShipWrapper>
  );
};

export { MultiAddressQuantityDistributor };
export default MultiAddressQuantityDistributor;
